import { FilesIcon } from "lucide-react"
import { useEffect, useRef, useState } from "react"
import { usePostHog } from "posthog-js/react"
import { toast } from "sonner"
import { Button } from "~/components/ui/button"
import { SearchableSelect } from "~/components/ui/searchable-select"
import type { ContentTemplateKind } from "~/lib/enums"
import { confirmAction } from "~/lib/native-dialog"
import { normalizeIssueDescription } from "~/lib/utils"
import { api } from "~/trpc/react"

export function TemplatePicker({
	teamId,
	kind,
	value,
	onApply,
	disabled
}: {
	teamId: string
	kind: ContentTemplateKind
	value: string | null
	onApply: (content: string) => void
	disabled?: boolean
}) {
	const {
		data: templates,
		isLoading,
		error,
		refetch
	} = api.templates.list.useQuery(
		{ teamId, kind },
		{ enabled: !!teamId && !disabled }
	)
	const utils = api.useUtils()
	const posthog = usePostHog()
	const [applying, setApplying] = useState(false)
	const revision = useRef(0)
	useEffect(
		() => () => {
			revision.current++
		},
		[teamId, kind, value, disabled]
	)

	return (
		<SearchableSelect
			multiple={false}
			value=""
			disabled={disabled || applying}
			options={(templates ?? []).map((template) => ({
				value: template.id,
				label: template.name
			}))}
			empty={
				isLoading ? (
					"Loading templates…"
				) : error ? (
					<Button
						variant="ghost"
						onClick={() => {
							void refetch()
						}}
					>
						Unable to load templates. Retry
					</Button>
				) : templates?.length ? (
					"No templates found"
				) : (
					"No templates yet"
				)
			}
			onChange={async (id) => {
				if (disabled || applying) return
				const request = ++revision.current
				setApplying(true)
				try {
					const selected = await utils.templates.get.fetch(
						{ teamId, id, kind },
						{ staleTime: 0 }
					)
					if (request !== revision.current) return
					if (
						normalizeIssueDescription(value) &&
						!(await confirmAction({
							title: "Replace content?",
							message:
								"This template will replace the current editor content. Your other fields will not change."
						}))
					)
						return
					if (request !== revision.current) return
					onApply(selected.content)
					posthog.capture("content_template_used", {
						templateId: selected.id,
						kind: selected.kind,
						teamId
					})
				} catch (error) {
					toast.error(
						error instanceof Error ? error.message : "Unable to apply template."
					)
				} finally {
					setApplying(false)
				}
			}}
		>
			<Button
				variant="ghost"
				size="compact"
				pending={applying}
				aria-label="Use template"
				onClick={() => {
					void refetch()
				}}
			>
				<FilesIcon />
				Use template
			</Button>
		</SearchableSelect>
	)
}
